<script setup>
// Reference 4 — the four operational arrangement items: what a manager actually sees
// and gets, per VvE. Below the seam, so it scrolls plainly: this is the half of the
// page someone comes BACK to in order to compare, and one-screen-per-beat would make
// that worse, not better.
//
// A spec sheet behind an ink rail rather than cards. These four are read against each
// other — "does this match how we work?" — and a numbered column is what makes them
// scannable in that order.
import { ref, onMounted, onUnmounted } from "vue";
import { useMotion, reveal } from "@/lib/motion";

const items = [
  {
    n: "05",
    term: "Machtigingsdekking als één getal",
    body: "Geen gevoel maar een verhouding: 34 van de 36 actieve incasso's hebben een geldige machtiging. Klopt die niet, dan weet je hoeveel eenheden je moet naplopen.",
  },
  {
    n: "06",
    term: "Opvolgen",
    body: "Per VvE alleen de uitzonderingen: een mislukte afschrijving of een terugboeking, met eenheid en eigenaar, en per betaling een gebeurtenissenlog. De eigenaar heeft dan al bericht van Pandig gehad, en een mislukte afschrijving wordt een paar werkdagen later automatisch nog één keer aangeboden; wil je niet wachten, dan bied je met één klik zelf aan. De volledige betaalhistorie en een trend over zes maanden staan ernaast.",
  },
  {
    n: "07",
    term: "Voorlopig, daarna definitief",
    body: "Een geslaagde incasso staat zes werkdagen als voorlopig binnen en is pas daarna definitief, omdat een terugboeking laat kan komen.",
  },
  {
    n: "08",
    term: "Elke betaling als pdf",
    body: "Per betaling een betaalspecificatie op naam van de VvE — bedrag, eenheid, periode, machtiging en settlement. Geen btw-factuur, want een bijdrage is geen btw-prestatie; de Pandig-vergoeding zelf krijg je per VvE wél als btw-factuur, ook als pdf.",
  },
];

const root = ref(null);
const rows = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    reveal(rows.value, { stagger: 0.08, y: 16, trigger: root.value, start: "top 80%" });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section id="wat-je-ziet" ref="root" class="border-t-2 border-foreground bg-card">
    <div class="mx-auto w-full max-w-6xl px-5 py-10 sm:py-16">
      <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
        Wat je per VvE ziet en krijgt.
      </h2>

      <dl class="mt-6 border-l-2 border-foreground sm:mt-10">
        <div
          v-for="(item, i) in items"
          :key="item.n"
          :ref="(el) => (rows[i] = el)"
          class="grid gap-x-5 gap-y-1 border-b-2 border-foreground/10 py-4 pl-4 last:border-b-0 sm:grid-cols-[3rem_16rem_1fr] sm:py-6 sm:pl-6"
        >
          <span class="font-mono text-sm font-semibold tabular-nums text-primary sm:text-base">{{ item.n }}</span>
          <dt class="font-sans text-base font-semibold tracking-tight text-foreground sm:text-lg">{{ item.term }}</dt>
          <dd class="text-sm leading-relaxed text-muted-foreground sm:text-base">{{ item.body }}</dd>
        </div>
      </dl>

      <p class="mt-6 text-sm leading-relaxed text-muted-foreground sm:mt-8 sm:text-base">
        Hoe die stroom er voor één VvE uitziet, staat op de homepage bij
        <RouterLink to="/#hoe-het-werkt" class="font-semibold text-primary underline-offset-4 hover:underline">hoe het werkt</RouterLink>
        en
        <RouterLink to="/#geldstroom" class="font-semibold text-primary underline-offset-4 hover:underline">de geldstroom</RouterLink>.
      </p>
    </div>
  </section>
</template>
